import * as React from 'react'
import './style.less'
import {IServicePerformanceAppState} from '@telstra-connect/service-performance/state'
import {ILoadSitesMapDataRequest} from '@telstra-connect/service-performance/state/sites-map-data'
import {loadSitesMapData} from '@telstra-connect/service-performance/methods/load-sites-map-data'
import * as MapInfo from '@telstra-connect/shared-payloads/map-indicator'
import {connect} from 'react-redux'
import {ISignInState} from '@telstra-connect/login/state'
import {AsyncActionState} from '@telstra-connect/standard-actions/async-action'
import {timer} from '@telstra-connect/utils/events/timer'
import {chain} from 'lodash'
import {
  optimalStatusMarker,
  issuesStatusMarker,
  degradedStatusMarker,
  noDataStatusMarker,
} from '@images'
import {MapMarkerError} from '@applications/service-performance/components/maps/map-marker-error'
import {NoMarkerData} from '@applications/service-performance/components/maps/map-marker-no-data'
import {NoMarkerDataInt} from '@applications/service-performance/components/maps/map-marker-no-data-int'
import Skeleton from 'telstra-ui/components/skeleton/Skeleton'
import {
  requestFailed,
  requestStarted,
  requestSucceeded,
} from '@telstra-connect/utils/data/request-helpers'
import {ITimeRange} from '@telstra-connect/service-performance/state/time-ranges'
import {ICustomerProfileState} from '@telstra-connect/tokens/state'
import {
  getCompanyId,
  isInternationalCustomer,
  isInternationalWorkFlow,
} from '@telstra-connect/config/config'
import {IAuthorizedRequestState} from '@telstra-connect/utils/http/requests/request'
import {IServicesListIntRequest} from '@telstra-connect/service-performance/state/services-list-filter-int'
import {IServiceSearchFilterInernational} from '@telstra-connect/service-performance/state/service-filters-int'

const MapWithoutCluster = React.lazy(
  () =>
    import(
      /* webpackChunkName: "map-without-cluster" */ '@applications/service-performance/components/maps/map-without-cluster'
    ),
)

const ZINDEX_NODATA = 1000
const ZINDEX_OPTIMAL = 2000
const ZINDEX_DEGRADED = 3000
const ZINDEX_ISSUES = 4000

interface ISitesListStateProps {
  searchTermsCharacterLimit: number
  sites: ILoadSitesMapDataRequest
  filteredServices: IServicesListIntRequest
  refreshInterval: number
  user: ISignInState
  tokens: ICustomerProfileState
  userManagement: unknown
}

export interface IGlobalMapProps {
  width?: number | string
  height?: number | string
  className?: string
  showNavigation?: boolean
  showFullScreen?: boolean
  showPopup?: boolean
  zoomLevel?: number
}

interface ISiteListStateOwnProps extends IGlobalMapProps {
  searchInput?: string
  timeRange?: ITimeRange
  category?: string
  filterInt?: IServiceSearchFilterInernational
}

interface ISitesListDispatchProps {
  loadSitesMapData: (
    searchTerm?: string,
    timeRange?: ITimeRange,
    isInternational?: boolean,
    companyId?: string,
  ) => void
}

interface ISitesListState {
  sites?: MapInfo.ISiteInfo[]
  searchTerms: string
  refreshInterval?: number
  isAutorefreshInProgress: boolean
  error?: string
}
type SitesListProps = ISitesListStateProps & ISitesListDispatchProps & ISiteListStateOwnProps

type SitesListState = ISitesListState

class MapComponent extends React.Component<SitesListProps, SitesListState> {
  private unsubscribeTimer: () => void
  constructor(props) {
    super(props)
    this.state = {
      sites: [],
      searchTerms: '',
      isAutorefreshInProgress: false,
      error: '',
    }
    this.infoLoad = this.infoLoad.bind(this)
    this.handleAutoRefresh = this.handleAutoRefresh.bind(this)
  }

  public componentWillUnmount() {
    if (this.unsubscribeTimer) {
      this.unsubscribeTimer()
    }
  }

  public componentDidMount() {
    this.infoLoad()
    const {refreshInterval} = this.props
    timer.setRefreshInterval(refreshInterval)
    this.unsubscribeTimer = timer.subscribe(this.handleAutoRefresh)
  }

  public componentDidUpdate(prevProps: SitesListProps, prevState: ISitesListState) {
    const {searchInput, refreshInterval} = this.props
    if (prevProps.refreshInterval !== refreshInterval) {
      timer.setRefreshInterval(refreshInterval)
      timer.restart()
    }

    if (prevProps.searchInput !== searchInput) {
      this.onSearchInputUpdate(searchInput)
    }

    if (
      this.state.searchTerms !== prevState.searchTerms ||
      this.props.timeRange !== prevProps.timeRange
    ) {
      this.setState({sites: []})
      this.infoLoad()
    }
  }

  public UNSAFE_componentWillReceiveProps(newProps: SitesListProps) {
    const newStatus = newProps.sites.status
    const oldStatus = this.props.sites.status

    const companyId = getCompanyId(this.props.tokens, this.props.user, this.props.userManagement)
    const isInternational = isInternationalCustomer(companyId)

    if (newProps.sites.error && newProps.sites.error === 'Invalid Search Term provided.') {
      this.setState({
        error: 'Invalid Search Term provided.',
      })
    }
    // this.isSearchInvalid = newProps.sites.error && newProps.sites.error === 'Invalid Search Term provided.';
    // map Filters support code logic for International
    if (!isInternational) {
      if (newStatus === oldStatus) {
        return
      }
    }
    if (newStatus !== AsyncActionState.Success) {
      return
    }
    let requiredSites = []
    if (isInternational) {
      let filteredSites = []
      if (newProps.filteredServices.data.filteredSites.length > 0) {
        filteredSites = newProps.sites.data.sites.filter((ele) => {
          if (
            newProps.filteredServices.data.filteredSites.includes(ele.fullAddress?.slice(0, 80))
          ) {
            return ele
          }
        })
      } else {
        if (
          newProps.filteredServices.status === AsyncActionState.Success &&
          newProps.filteredServices.data.services.length == 0
        ) {
          filteredSites = []
        } else {
          filteredSites = newProps.sites.data.sites
        }
      }
      const uniqueSites = chain(filteredSites)
        .groupBy('fullAddress')
        .map((services, fullAddress) => {
          const address = fullAddress
          const accountId = services[0].accountId
          const lastPolledTime = services[0].lastPolledTime
          const location = services[0].location
          const wanAvailabilityStatus = services[0].wanAvailabilityStatus
          return {
            fullAddress: address,
            accountId,
            lastPolledTime,
            location,
            wanAvailabilityStatus,
            servicesInSite: services,
          }
        })
        .value()
      requiredSites = uniqueSites
    } else {
      requiredSites = newProps.sites.data.sites
    }
    const siteMapAddress = requiredSites.map((site) => {
      let iconName
      let zIndex
      // let iconColor
      let iconColorName
      if (site.wanAvailabilityStatus === 'Optimal') {
        iconName = optimalStatusMarker
        zIndex = ZINDEX_OPTIMAL
        iconColorName = 'GREEN'
        // iconColor = '#008A00'
      } else if (site.wanAvailabilityStatus === 'Issues') {
        iconName = issuesStatusMarker
        zIndex = ZINDEX_ISSUES
        iconColorName = 'RED'
        // iconColor = '#D0021B'
      } else if (site.wanAvailabilityStatus === 'Degraded') {
        iconName = degradedStatusMarker
        zIndex = ZINDEX_DEGRADED
        iconColorName = 'ORANGE'
        // iconColor = '#FF8000'
      } else if (site.wanAvailabilityStatus === 'No Data') {
        iconName = noDataStatusMarker
        zIndex = ZINDEX_NODATA
        iconColorName = 'BLUE'
        // iconColor = '#0064D2'
      }

      return {
        id: site.id,
        fullAddress: site.fullAddress,
        userReference: site.userReference,
        icon: iconName,
        location: site.location,
        iconColorName,
        zIndex,
        availabilityStatus: site.wanAvailabilityStatus,
        ...(isInternational ? {accountId: site.accountId} : {}),
      }
    })

    siteMapAddress.sort((site1, site2) => {
      return site1.zIndex - site2.zIndex
    })

    this.setState({
      sites: siteMapAddress,
    })
  }

  public handleAutoRefresh() {
    this.setState({
      isAutorefreshInProgress: true,
    })
    this.infoLoad(true)
  }

  public render() {
    const companyId = getCompanyId(this.props.tokens, this.props.user, this.props.userManagement)
    const isInternational = isInternationalCustomer(companyId)
    const {
      height = '440px',
      width,
      showNavigation,
      showFullScreen,
      showPopup,
      zoomLevel,
      sites,
    } = this.props

    if (requestStarted(this.props.sites)) {
      return <Skeleton count={1} width={width} height={height} />
    }

    if (requestSucceeded(this.props.sites) && this.state.sites.length === 0) {
      return isInternational ? <NoMarkerDataInt /> : <NoMarkerData />
    }

    if (requestFailed(this.props.sites)) {
      return <MapMarkerError onTryAgain={this.infoLoad} />
    }

    return (
      <React.Suspense fallback={<Skeleton count={1} width={width} height={height} />}>
        <MapWithoutCluster
          mapHeight={height}
          mapWidth={width}
          showNavigation={showNavigation ?? true}
          showFullScreen={showFullScreen ?? true}
          showPopup={showPopup ?? true}
          zoomLevel={zoomLevel}
          sites={this.state.sites}
          mapCentroid={sites?.data?.mapCentroid}
          isInternational={isInternational}
          category={this.props.category}
          filterInt={this.props.filterInt}
        />
      </React.Suspense>
    )
  }

  private infoLoad(checkAutoRefresh?: boolean) {
    if (!checkAutoRefresh) {
      this.setState({
        isAutorefreshInProgress: false,
      })
    }
    let searchTerms = ''
    if (this.state.searchTerms) {
      searchTerms = this.state.searchTerms
    }

    const companyId = getCompanyId(this.props.tokens, this.props.user, this.props.userManagement)
    const isInternational = isInternationalCustomer(companyId)
    const isInternationalDataLoad = isInternationalWorkFlow(isInternational) ? true : false
    this.props.loadSitesMapData(
      searchTerms,
      this.props.timeRange,
      isInternationalDataLoad,
      companyId,
    )
  }

  private onSearchInputUpdate = (input) => {
    const inputText = input.trim()
    if (inputText.length >= this.props.searchTermsCharacterLimit || inputText.length === 0) {
      this.setState({
        searchTerms: inputText,
      })
    }
  }
}

const mapStateToProps = (
  state: IServicePerformanceAppState & IAuthorizedRequestState,
): ISitesListStateProps => {
  return {
    sites: state.servicePerformance.sitesMapData,
    filteredServices: state.servicePerformance.servicesListFilterInt,
    searchTermsCharacterLimit:
      state.servicePerformance.sitesMapData.data.searchTermsCharacterLimit || 0,
    refreshInterval: state.servicePerformance.sitesMapData.data.refreshInterval,
    tokens: state.tokens,
    user: state.user,
    userManagement: state.userManagement,
  }
}

const mapDispatchToProps = (dispatch): ISitesListDispatchProps => {
  return {
    loadSitesMapData: (
      searchTerm?: string,
      timeRange?: ITimeRange,
      isInternational?: boolean,
      companyId?: string,
    ) => dispatch(loadSitesMapData(searchTerm, timeRange, isInternational, companyId)),
  }
}

const Map = connect<
  ISitesListStateProps,
  ISitesListDispatchProps,
  {},
  IServicePerformanceAppState & IAuthorizedRequestState
>(
  mapStateToProps,
  mapDispatchToProps,
)(MapComponent)

export default Map
